import React, { createContext, useCallback, useContext, useMemo, useReducer, useState } from 'react';
import { clarity } from '../lib/clarity';

const CartContext = createContext();

// Ally-catalog products and regular Products both use plain numeric ids from
// independent sequences, so an id alone can't identify a cart line — pair it
// with `source` ('ally' or undefined for regular products). A color variant
// of the same product is its own line too, so variantId joins the key —
// two different colors of the same product must never merge quantities.
const cartItemKey = (item) => `${item.source || 'product'}-${item.id}-${item.variantId || ''}`;

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      const existingItem = state.items.find(item => cartItemKey(item) === cartItemKey(action.payload));
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            cartItemKey(item) === cartItemKey(action.payload)
              ? { ...item, quantity: item.quantity + 1 }
              : item
          ),
          totalItems: state.totalItems + 1
        };
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload, quantity: 1 }],
        totalItems: state.totalItems + 1
      };

    case 'REMOVE_FROM_CART': {
      const removedItem = state.items.find(item => cartItemKey(item) === action.payload);
      return {
        ...state,
        items: state.items.filter(item => cartItemKey(item) !== action.payload),
        totalItems: state.totalItems - (removedItem?.quantity ?? 1)
      };
    }

    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        totalItems: 0
      };

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    totalItems: 0
  });
  // Bumped on every successful addToCart — Header watches this to briefly
  // reveal the mobile bag menu so the user sees the item landed in their bag.
  const [cartOpenSignal, setCartOpenSignal] = useState(0);

  // `variant`, when passed, is one entry from product.colorVariants — the
  // color the shopper picked on the product page. It's optional: products
  // without color variants call addToCart(product) exactly as before.
  const addToCart = useCallback((product, variant) => {
    // Sold out is per-variant when a variant is selected, otherwise the
    // product's own flag (kept in sync with the variant aggregate server-side).
    if (variant ? variant.isSoldOut : product.isSoldOut) {
      return; // Don't add if sold out
    }

    // Don't carry the full variant catalog into every cart line.
    const { colorVariants, ...productRest } = product;

    // Use presale price (50% of price) for preorder, otherwise use regular price
    const productWithCorrectPrice = {
      ...productRest,
      price: product.isPreorder ? (product.price || 0) * 0.5 : product.price,
      variantId: variant?.id || null,
      variantName: variant?.name || null,
      variantName_en: variant?.name_en || null,
      variantImage: variant?.image || null,
    };
    dispatch({ type: 'ADD_TO_CART', payload: productWithCorrectPrice });
    clarity.event('add_to_cart');
    setCartOpenSignal(Date.now());
  }, [dispatch]);

  const removeFromCart = useCallback((productId, source, variantId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: cartItemKey({ id: productId, source, variantId }) });
  }, [dispatch]);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, [dispatch]);

  const getTotalPrice = useCallback(() => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  }, [state.items]);

  const value = useMemo(() => ({
    ...state,
    addToCart,
    removeFromCart,
    clearCart,
    getTotalPrice,
    cartOpenSignal,
  }), [state, addToCart, removeFromCart, clearCart, getTotalPrice, cartOpenSignal]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

